import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, Modal } from 'semantic-ui-react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import {
  composeValidators,
  mustBeNumber,
  mustBePositiveNumber,
  mustNotBeZero,
  validateOnlyIfPresent
} from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import {
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  withEntriesContext
} from 'components/segments/entires/EntriesContext';
import { numberFormatter } from 'utils/fomattingUtils';
import { getAllMedicamentDosagesByMedicamentId } from 'services/dosageServices';
import axios, { CancelTokenSource } from 'axios';
import { noop } from 'utils/functionUtils';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import TextArea from 'components/final-form/TextArea';

interface Medicament {
  dosageId: number,
  quantity: number,
  value: number,
  rate: number,
  remarks: string
}

interface Props extends MessageSourceProps, EntriesConsumerRenderProps {
  modalOpen: boolean,
  onCloseModal: () => void,
  medicament: ExtendedBaseEntryDto
}

interface State {
  medicament: Partial<Medicament>,
  dosages: Array<DropdownOption>,
  cancelTokenSource: CancelTokenSource
}

class MedicamentModal extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {medicament} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    let editMedicament: Partial<Medicament> = {};

    if (medicament) {
      editMedicament = {
        quantity: medicament.quantity,
        value: medicament.actualValue != medicament.value ? medicament.value : undefined,
        rate: medicament.percentage,
        dosageId: medicament.dosageId,
        remarks: medicament.remarks
      };
    }

    this.state = {
      medicament: editMedicament,
      dosages: [],
      cancelTokenSource
    };

    this.getAllDosages();
  }

  getAllDosages = () => {

    const {medicament} = this.props;
    const {cancelTokenSource} = this.state;

    if (medicament) {
      getAllMedicamentDosagesByMedicamentId(medicament.medicamentId, cancelTokenSource)
          .then(response => {
            this.setState({
              dosages: mapToDropdownOptionArray(response, "description")
            })
          }).catch(noop);
    }
  };

  handleSubmit = async (values: Partial<Medicament>) => {

    const {onCloseModal, saveEntry, medicament} = this.props;

    let value = medicament.actualValue;
    let calculatedValue = medicament.actualValue * medicament.taxPoint;

    if (values.value) {
      value = +values.value;
    }

    if (values.rate) {
      calculatedValue = +values.rate > 0 ? (value * medicament.taxPoint) * (+values.rate / 100) : 0;
    } else {
      calculatedValue = value * medicament.taxPoint;
    }

    const modifiedMedicament = {
      ...medicament,
      quantity: values.quantity ? +values.quantity : 1,
      value: value,
      percentage: values.rate ? +values.rate : undefined,
      calculatedValue: calculatedValue,
      dosageId: values.dosageId,
      remarks: values.remarks
    };

    await saveEntry(modifiedMedicament);
    onCloseModal();
  };

  render(): React.ReactNode {

    const {modalOpen, onCloseModal, getMessage} = this.props;
    const {medicament} = this.state;

    return (
        <Modal
            open={modalOpen}
            onClose={onCloseModal}
            style={{width: "515px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("medicamentModal.header")}</Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                subscription={{pristine: true, submitting: true}}
                render={this.renderForm}
                initialValues={medicament}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

  renderForm = ({submitting, handleSubmit}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCloseModal} = this.props;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {this.renderValueSegment()}
              {this.renderDosageSelectionSegment()}
            </FormContent>
            <FormButtonsRow>
              <Button type="submit" compact primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button type="button" disabled={submitting}
                      onClick={onCloseModal}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderValueSegment = (): React.ReactNode => {

    const {getMessage, medicament} = this.props;

    const validator = validateOnlyIfPresent(composeValidators(mustBeNumber, mustBePositiveNumber, mustNotBeZero));

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("quantityAndValue")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4} verticalAlign="middle">
              {`${getMessage("quantity")}:`}
            </Grid.Column>
            <Grid.Column width={12} verticalAlign="middle">
              <Field
                  name="quantity"
                  component={Input}
                  validate={validator}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4} verticalAlign="middle">
              {`${getMessage("value")}:`}
            </Grid.Column>
            <Grid.Column width={12} verticalAlign="middle">
              <Field
                  name="value"
                  component={Input}
                  validate={validator}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4} verticalAlign="middle">
              {`${getMessage("originalValue")}:`}
            </Grid.Column>
            <Grid.Column width={12} verticalAlign="middle">
              {`${numberFormatter.format(medicament.actualValue)} ${medicament.type}`}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4} verticalAlign="middle">
              {`${getMessage("rate")}:`}
            </Grid.Column>
            <Grid.Column width={12} verticalAlign="middle">
              <Field
                  name="rate"
                  component={Input}
                  validate={validateOnlyIfPresent(composeValidators(mustBeNumber, mustBePositiveNumber))}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    )
  };

  renderDosageSelectionSegment = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {dosages} = this.state;

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("medicamentModal.dosageSelection")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}></Grid.Column>
            <Grid.Column width={12}>
              <Field
                  name="dosageId"
                  component={Select}
                  options={dosages}
                  clearable
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              {getMessage("medicamentModal.remark")}
            </Grid.Column>
            <Grid.Column width={12}>
              <Field
                  name="remarks"
                  component={TextArea}
                  style={{maxHeight: "70vh"}}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };
}

export default withMessages(withEntriesContext(MedicamentModal));
